// src/components/sessions/SessionReportPanel.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  PaperAirplaneIcon,
  PlusIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import sessionService from '@/services/sessionService';
import ReportFormModal from './ReportFormModal';

export default function SessionReportPanel({ sessionId, userRole, attendanceDetails }) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const hasFetched = useRef(false);

  // Modal state
  const [showFormModal, setShowFormModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Send state
  const [sendingId, setSendingId] = useState(null);

  const isTeacher = userRole === 'teacher';

  const fetchReports = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError('');
    try {
      const result = await sessionService.getSessionReports(sessionId);
      if (result.success) {
        setReports(result.data || []);
      } else {
        setError(result.message || t('reports.fetchError'));
      }
    } catch (err) {
      console.error('Failed to fetch reports:', err);
      setError(t('reports.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [sessionId, t]);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchReports();
  }, [fetchReports]);

  // Students who don't have a report yet
  const studentsWithoutReport = React.useMemo(() => {
    if (!isTeacher || !attendanceDetails) return [];
    const reportedStudentIds = new Set(reports.map(r => r.student_institution_member_id));
    return attendanceDetails
      .filter(p => p.role === 'student' && !reportedStudentIds.has(p.institution_member_id))
      .sort((a, b) => a.display_name.localeCompare(b.display_name));
  }, [isTeacher, attendanceDetails, reports]);

  const totalStudents = attendanceDetails
    ? attendanceDetails.filter(p => p.role === 'student').length
    : 0;

  const handleCreate = () => {
    setShowFormModal(true);
  };

  const handleSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      const result = await sessionService.createReport(sessionId, formData);
      if (result.success) {
        setShowFormModal(false);
        hasFetched.current = false;
        await fetchReports();
      } else {
        alert(result.message || t('reports.saveFailed'));
      }
    } catch (err) {
      alert(t('reports.saveFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendEmail = async (reportId) => {
    setSendingId(reportId);
    try {
      const result = await sessionService.sendReportEmail(sessionId, reportId);
      if (result.success) {
        hasFetched.current = false;
        await fetchReports();
      } else {
        alert(result.message || t('reports.sendFailed'));
      }
    } catch (err) {
      alert(t('reports.sendFailed'));
    } finally {
      setSendingId(null);
    }
  };

  // Reports are for parents only — don't show to students
  if (!isTeacher) return null;

  return (
    <>
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <DocumentTextIcon className="w-4 h-4 text-slate-500" />
            <span className="font-medium text-sm text-slate-700">
              {t('reports.title')}
            </span>
            {isTeacher && totalStudents > 0 && (
              <span className="text-xs px-2 py-1 bg-white rounded-full text-slate-600">
                {t('reports.summary', { count: reports.length, total: totalStudents })}
              </span>
            )}
            {!isTeacher && reports.length > 0 && (
              <span className="text-xs px-2 py-1 bg-cyan-100 text-cyan-700 rounded-full">
                {t('reports.available')}
              </span>
            )}
          </div>
          <div className="text-slate-400">
            {isExpanded ? <ChevronDownIcon className="w-5 h-5" /> : <ChevronRightIcon className="w-5 h-5" />}
          </div>
        </button>

        {isExpanded && (
          <div className="p-4 bg-white">
            {loading && (
              <p className="text-sm text-slate-500">{t('common.loading')}</p>
            )}

            {error && (
              <p className="text-sm text-red-600 mb-3">{error}</p>
            )}

            {!loading && !error && (
              <div className="space-y-3">
                {/* Teacher: Write report button */}
                {isTeacher && studentsWithoutReport.length > 0 && (
                  <button
                    onClick={handleCreate}
                    className="w-full px-3 py-2 border-2 border-dashed border-slate-300 rounded-lg text-sm text-slate-600 hover:border-cyan-400 hover:text-cyan-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <PlusIcon className="w-4 h-4" />
                    {t('reports.writeReport')} ({studentsWithoutReport.length} {t('reports.studentsRemaining')})
                  </button>
                )}

                {reports.length === 0 && !isTeacher && (
                  <p className="text-sm text-slate-500">{t('reports.noReports')}</p>
                )}

                {reports.length === 0 && isTeacher && studentsWithoutReport.length === 0 && (
                  <p className="text-sm text-slate-500">{t('reports.noStudents')}</p>
                )}

                {reports.map(report => (
                  <div key={report.id} className="border border-slate-200 rounded-lg p-3">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        {isTeacher && (
                          <span className="text-sm font-medium text-slate-800">
                            {report.student_name}
                          </span>
                        )}
                        {!isTeacher && report.teacher_name && (
                          <span className="text-xs text-slate-500">
                            {t('reports.writtenBy', { name: report.teacher_name })}
                          </span>
                        )}
                      </div>

                      {isTeacher && (
                        <div className="flex items-center gap-1.5">
                          {report.email_sent ? (
                            <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded flex items-center gap-1" title={report.email_recipient}>
                              <CheckCircleIcon className="w-3 h-3" />
                              {t('reports.emailSent')}
                            </span>
                          ) : report.student_parent_email ? (
                            <button
                              onClick={() => handleSendEmail(report.id)}
                              disabled={sendingId === report.id}
                              className="text-xs px-2 py-1 bg-cyan-50 text-cyan-700 hover:bg-cyan-100 rounded transition-colors flex items-center gap-1 disabled:opacity-50"
                              title={t('reports.sendEmail')}
                            >
                              <PaperAirplaneIcon className="w-3 h-3" />
                              {sendingId === report.id ? t('reports.sending') : t('reports.sendEmail')}
                            </button>
                          ) : (
                            <span className="text-xs px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded">
                              {t('reports.noParentEmail')}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Report content */}
                    <p className="text-sm text-slate-700 whitespace-pre-line">
                      {report.report_content}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Report form modal — create only, no editing */}
      <ReportFormModal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        onSubmit={handleSubmit}
        students={studentsWithoutReport}
        isSubmitting={isSubmitting}
      />
    </>
  );
}
